import React, { Fragment, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Icon,
  IconButton,
  Tooltip,
  Chip,
  alpha,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  Tabs,
  Tab,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { grnService } from "../../services/grn.service";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import {
  IESDoc,
  TableColumn,
  WithId,
  FilterGroup,
} from "../../../../app.interface";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { AppTable } from "../../../../components/AppTable";
import { AppSearch } from "../../../../components/AppSearch";
import { useAuth } from "../../../auth/hooks/useAuth";
import {
  GoodsReceiptNote,
  GRNStatus,
  ListGRNsRequest,
  GRNActivityLog,
  ApproveGRNRequest,
  ApproveGRNResponse,
  RejectGRNRequest,
  MarkInvoicedRequest,
} from "../../types/grn.types";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { GRNActivityTimeline } from "../../components/ActivityTimeline/GRNActivityTimeline";
import { EnhancedApprovalDialog } from "../../components/GRNForm/EnhancedApprovalDialog";
import { hasPermission } from "../../../auth/utils/permission.utils";

// Status configuration for GRN
const grnStatusConfig: Record<GRNStatus, { label: string; color: any }> = {
  draft: { label: "Draft", color: "default" },
  pending_approval: { label: "Pending Approval", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  invoiced: { label: "Invoiced", color: "info" },
};

// Approval Dialog Component
const ApprovalDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  grn: GoodsReceiptNote | null;
  onSuccess: () => void;
}> = ({ open, onClose, grn, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");

  const handleApprove = async () => {
    if (!grn) return;

    setLoading(true);
    try {
      const params: ApproveGRNRequest = {
        _id: grn._id,
        approval_notes: approvalNotes.trim() || undefined,
      };

      const response = await grnService.approveGRN(params);
      if (response.success) {
        toast.success("GRN approved successfully. Inventory has been updated.");
        onSuccess();
        onClose();
        setApprovalNotes("");
      }
    } catch (error: any) {
      console.error("Error approving GRN:", error);
      toast.error(error.message || "Failed to approve GRN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve GRN</DialogTitle>
      <DialogContent>
        {grn && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Approving this GRN will create inventory entries and mark the
              linked Purchase Order as "Ready for Invoice".
            </Alert>

            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                GRN Details
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    GRN Number
                  </Typography>
                  <Typography variant="body2">{grn.grn_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    PO Number
                  </Typography>
                  <Typography variant="body2">{grn.po_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Vendor
                  </Typography>
                  <Typography variant="body2">
                    {grn.po_reference.vendor_details.supplier_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Total Value
                  </Typography>
                  <Typography variant="body2">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: grn.currency,
                    }).format(grn.total_received_value)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <TextField
              fullWidth
              label="Approval Notes (Optional)"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Add any notes about the approval..."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleApprove}
          loading={loading}
          variant="contained"
          color="success"
        >
          Approve GRN
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// Rejection Dialog Component
const RejectionDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  grn: GoodsReceiptNote | null;
  onSuccess: () => void;
}> = ({ open, onClose, grn, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const handleReject = async () => {
    if (!grn || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setLoading(true);
    try {
      const params: RejectGRNRequest = {
        _id: grn._id,
        rejection_reason: rejectionReason.trim(),
      };

      const response = await grnService.rejectGRN(params);
      if (response.success) {
        toast.success("GRN rejected successfully");
        onSuccess();
        onClose();
        setRejectionReason("");
      }
    } catch (error: any) {
      console.error("Error rejecting GRN:", error);
      toast.error(error.message || "Failed to reject GRN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject GRN</DialogTitle>
      <DialogContent>
        {grn && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Rejecting this GRN will prevent inventory creation. Please provide
              a clear reason.
            </Alert>

            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                GRN Details
              </Typography>
              <Typography variant="body2">
                {grn.grn_number} - {grn.po_number}
              </Typography>
            </Paper>

            <TextField
              fullWidth
              label="Rejection Reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={3}
              required
              placeholder="Please explain why this GRN is being rejected..."
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleReject}
          loading={loading}
          variant="contained"
          color="error"
        >
          Reject GRN
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

// Mark as Invoiced Dialog Component
const MarkInvoicedDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  grn: GoodsReceiptNote | null;
  onSuccess: () => void;
}> = ({ open, onClose, grn, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [invoiceReference, setInvoiceReference] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [invoiceNotes, setInvoiceNotes] = useState("");

  const handleMarkInvoiced = async () => {
    if (!grn || !invoiceReference.trim()) {
      toast.error("Please provide an invoice reference");
      return;
    }

    setLoading(true);
    try {
      const params: MarkInvoicedRequest = {
        _id: grn._id,
        invoice_reference: invoiceReference.trim(),
        invoice_date: dayjs(invoiceDate).valueOf(),
        invoice_notes: invoiceNotes.trim() || undefined,
      };

      const response = await grnService.markAsInvoiced(params);
      if (response.success) {
        toast.success("GRN marked as invoiced successfully");
        onSuccess();
        onClose();
        // Reset form
        setInvoiceReference("");
        setInvoiceDate(dayjs().format("YYYY-MM-DD"));
        setInvoiceNotes("");
      }
    } catch (error: any) {
      console.error("Error marking GRN as invoiced:", error);
      toast.error(error.message || "Failed to mark GRN as invoiced");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mark as Invoiced</DialogTitle>
      <DialogContent>
        {grn && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Mark this GRN as invoiced by providing the invoice reference and
              details.
            </Alert>

            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                GRN Details
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    GRN Number
                  </Typography>
                  <Typography variant="body2">{grn.grn_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    PO Number
                  </Typography>
                  <Typography variant="body2">{grn.po_number}</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                fullWidth
                label="Invoice Reference *"
                value={invoiceReference}
                onChange={(e) => setInvoiceReference(e.target.value)}
                placeholder="Enter invoice number..."
                required
              />

              <TextField
                fullWidth
                label="Invoice Date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Invoice Notes (Optional)"
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any notes about the invoice..."
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          onClick={handleMarkInvoiced}
          loading={loading}
          variant="contained"
          color="primary"
        >
          Mark as Invoiced
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const GRNList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  // Data state
  const [grns, setGRNs] = useState<GoodsReceiptNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [markInvoicedDialogOpen, setMarkInvoicedDialogOpen] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState<GoodsReceiptNote | null>(null);
  const [selectedGRNDetails, setSelectedGRNDetails] =
    useState<GoodsReceiptNote | null>(null);

  // Pagination and filtering
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, any>>({});

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuGRNId, setMenuGRNId] = useState<string | null>(null);

  // Activity logs states (for drawer)
  const [activityLogs, setActivityLogs] = useState<GRNActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);

  // Load GRNs
  const loadGRNs = async () => {
    setLoading(true);
    try {
      const params: ListGRNsRequest = {
        pageIndex: page,
        pageSize,
        sortField,
        sortOrder,
        searchQuery: search || undefined,
        status: filters.status || undefined,
        warehouse_id: filters.warehouse_id || undefined,
        po_id: filters.po_id || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        dateField: filters.dateField || "created_at",
      };

      const response = await grnService.listGRNs(params);
      if (response.success && response.data) {
        setGRNs(response.data.grns || []);
        setTotalItems(response.data.totalCount || 0);
      } else {
        throw new Error(response.error?.message || "Failed to load GRNs");
      }
    } catch (error: any) {
      console.error("Error loading GRNs:", error);
      toast.error(error.message || "Failed to load GRNs");
      setGRNs([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Load activity logs for selected GRN
  const loadActivityLogs = async (grn: GoodsReceiptNote) => {
    setActivityLoading(true);
    try {
      const response = await grnService.getGRNActivityLogs({
        grn_id: grn._id,
        pageIndex: 0,
        pageSize: 50,
      });
      if (response.success && response.data) {
        setActivityLogs(response.data.activity_logs || []);
      }
    } catch (error: any) {
      console.error("Error loading activity logs:", error);
      toast.error("Failed to load activity logs");
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    loadGRNs();
  }, [page, pageSize, sortField, sortOrder, search, filters]);

  // Table columns
  const columns: TableColumn[] = [
    {
      id: "grn_number",
      header: "GRN Number",
      field: "grn_number",
      type: "text",
      sortable: true,
    },
    {
      id: "po_number",
      header: "PO Number",
      field: "po_number",
      type: "text",
      sortable: true,
    },
    {
      id: "vendor",
      header: "Vendor",
      field: "vendor", // field is not used since we provide an accessor
      type: "text",
      sortable: true,
      accessor: (row: Record<string, any>) =>
        row?.po_reference?.vendor_details?.supplier_name || "N/A",
    },
    {
      id: "warehouse",
      header: "Warehouse",
      field: "warehouse_name",
      type: "text",
      sortable: true,
    },
    {
      id: "total_value",
      header: "Total Value",
      field: "total_received_value",
      type: "money",
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      field: "status",
      type: "status",
      statusConfig: grnStatusConfig,
      sortable: true,
    },
    {
      id: "received_date",
      header: "Received Date",
      field: "received_date",
      type: "datetime",
      dateFormat: "relative",
      sortable: true,
    },
    {
      id: "actions",
      header: "Actions",
      field: "actions",
      type: "action",
      renderAction: (row: WithId & Record<string, any>) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuGRNId(row._id);
          }}
        >
          <Icon>more_vert</Icon>
        </IconButton>
      ),
    },
  ];

  // Filter groups
  const filterGroups: FilterGroup[] = [
    {
      id: "status",
      label: "Status",
      type: "select",
      options: Object.entries(grnStatusConfig).map(([value, config]) => ({
        id: value,
        label: config.label,
        value,
      })),
    },
    {
      id: "dateField",
      label: "Date Field",
      type: "select",
      options: [
        { id: "created_at", label: "Created Date", value: "created_at" },
        { id: "received_date", label: "Received Date", value: "received_date" },
        { id: "updated_at", label: "Updated Date", value: "updated_at" },
      ],
    },
  ];

  // Event handlers
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleRowClick = (row: WithId & Record<string, any>) => {
    const grn = grns.find((g) => g._id === row._id);
    if (grn) {
      setSelectedGRNDetails(grn);
      loadActivityLogs(grn);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuGRNId(null);
  };

  const handleCreateGRN = () => {
    navigate("/business/procurement/grn/new");
  };

  const handleEditGRN = (grn: GoodsReceiptNote) => {
    navigate(`/business/procurement/grn/${grn._id}/edit`);
  };

  const handleApproveGRN = (grn: GoodsReceiptNote) => {
    setSelectedGRN(grn);
    setApprovalDialogOpen(true);
    handleMenuClose();
  };

  const handleRejectGRN = (grn: GoodsReceiptNote) => {
    setSelectedGRN(grn);
    setRejectionDialogOpen(true);
    handleMenuClose();
  };

  const handleMarkInvoiced = (grn: GoodsReceiptNote) => {
    setSelectedGRN(grn);
    setMarkInvoicedDialogOpen(true);
    handleMenuClose();
  };

  const handleCreateInvoice = (grn: GoodsReceiptNote) => {
    navigate(`/business/procurement/invoices/new?grn_id=${grn._id}`);
    handleMenuClose();
  };

  const handleSendForApproval = async (grn: GoodsReceiptNote) => {
    handleMenuClose();
    setSaving(true);

    try {
      // Use dedicated send-for-approval endpoint
      const response = await grnService.sendForApproval({
        _id: grn._id,
        notes: `GRN ${grn.grn_number} sent for approval`,
      });

      if (response.success) {
        toast.success("GRN sent for approval successfully");
        loadGRNs();
      }
    } catch (error: any) {
      console.error("Error sending GRN for approval:", error);
      toast.error(error.message || "Failed to send GRN for approval");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGRN = (grn: GoodsReceiptNote) => {
    setSelectedGRN(grn);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteGRN = async () => {
    if (!selectedGRN) return;

    setSaving(true);
    try {
      const response = await grnService.deleteGRN({ _id: selectedGRN._id });
      if (response.success) {
        toast.success("GRN deleted successfully");
        loadGRNs();
        setDeleteDialogOpen(false);
        setSelectedGRN(null);
      }
    } catch (error: any) {
      console.error("Error deleting GRN:", error);
      toast.error(error.message || "Failed to delete GRN");
    } finally {
      setSaving(false);
    }
  };

  const handleDrawerClose = () => {
    setSelectedGRNDetails(null);
    setActivityLogs([]);
    setDrawerTab(0);
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const selectedMenuGRN = grns.find((grn) => grn._id === menuGRNId);

  return (
    <Fragment>
      <PageHeader
        title="Goods Receipt Notes"
        subtitle="Manage goods receipt notes for received purchase orders • Click any row to view details"
        icon="inventory"
        actions={
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleCreateGRN}
            disabled={
              !hasPermission(
                "inventory.grn.create",
                user?.newPermissions,
                user?.assignment_details?.id,
                user?.is_store_owner
              )
            }
          >
            Create GRN
          </Button>
        }
      />

      <Box sx={{ mb: 3 }}>
        <AppSearch
          onSearch={setSearch}
          onFilterChange={setFilters}
          filterGroups={filterGroups}
          placeholder="Search GRNs by number, PO, vendor..."
        />
      </Box>

      {loading || grns.length > 0 ? (
        <AppTable
          columns={columns}
          data={grns}
          loading={loading}
          pagination={{
            total: totalItems,
            page,
            pageSize,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          onSort={(field: string, direction: "asc" | "desc") => {
            setSortField(field);
            setSortOrder(direction);
          }}
          onRowClick={handleRowClick}
          toolbarActions={{
            export: {
              label: "Export",
              is_disabled: grns.length === 0,
            },
          }}
        />
      ) : (
        <EmptyState
          title="No Goods Receipt Notes"
          description="Create your first GRN to receive items from purchase orders"
          icon="inventory"
          actionText="Create GRN"
          onAction={handleCreateGRN}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedMenuGRN && (
          <>
            <MenuItem
              onClick={() => handleEditGRN(selectedMenuGRN)}
              disabled={
                !hasPermission(
                  "inventory.grn.edit",
                  user?.newPermissions,
                  user?.assignment_details?.id,
                  user?.is_store_owner
                )
              }
            >
              <Icon sx={{ mr: 1 }}>edit</Icon>
              Edit
            </MenuItem>

            {selectedMenuGRN.status === "draft" && (
              <MenuItem
                onClick={() => handleSendForApproval(selectedMenuGRN)}
                disabled={
                  !hasPermission(
                    "inventory.grn.submit",
                    user?.newPermissions,
                    user?.assignment_details?.id,
                    user?.is_store_owner
                  )
                }
              >
                <Icon sx={{ mr: 1, color: "primary.main" }}>send</Icon>
                Send for Approval
              </MenuItem>
            )}

            {selectedMenuGRN.status === "pending_approval" && (
              <>
                <MenuItem
                  onClick={() => handleApproveGRN(selectedMenuGRN)}
                  disabled={
                    !hasPermission(
                      "inventory.grn.approve",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon sx={{ mr: 1, color: "success.main" }}>
                    check_circle
                  </Icon>
                  Approve
                </MenuItem>
                <MenuItem
                  onClick={() => handleRejectGRN(selectedMenuGRN)}
                  disabled={
                    !hasPermission(
                      "inventory.grn.reject",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon sx={{ mr: 1, color: "error.main" }}>cancel</Icon>
                  Reject
                </MenuItem>
              </>
            )}

            {selectedMenuGRN.status === "approved" && (
              <>
                <MenuItem
                  onClick={() => handleCreateInvoice(selectedMenuGRN)}
                  disabled={
                    !hasPermission(
                      "inventory.invoices.create",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon sx={{ mr: 1, color: "success.main" }}>
                    receipt_long
                  </Icon>
                  Create Invoice
                </MenuItem>
                <MenuItem
                  onClick={() => handleMarkInvoiced(selectedMenuGRN)}
                  disabled={
                    !hasPermission(
                      "inventory.grn.mark_as_invoiced",
                      user?.newPermissions,
                      user?.assignment_details?.id,
                      user?.is_store_owner
                    )
                  }
                >
                  <Icon sx={{ mr: 1, color: "primary.main" }}>receipt</Icon>
                  Mark as Invoiced
                </MenuItem>
              </>
            )}

            {(selectedMenuGRN.status === "draft" ||
              selectedMenuGRN.status === "rejected") && (
              <>
                <Divider />
                <MenuItem
                  onClick={() => handleDeleteGRN(selectedMenuGRN)}
                  sx={{ color: "error.main" }}
                >
                  <Icon sx={{ mr: 1 }}>delete</Icon>
                  Delete
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* GRN Details Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedGRNDetails)}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 600, md: 700 } },
        }}
      >
        {selectedGRNDetails && (
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h5" fontWeight={600}>
                Goods Receipt Note Details
              </Typography>
              <IconButton onClick={handleDrawerClose}>
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Tabs
              value={drawerTab}
              onChange={(_, newValue) => setDrawerTab(newValue)}
              sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Overview" />
              <Tab label="Items" />
              <Tab label="Activity" />
            </Tabs>

            {drawerTab === 0 && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        GRN Number
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {selectedGRNDetails.grn_number}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={
                            grnStatusConfig[selectedGRNDetails.status].label
                          }
                          color={
                            grnStatusConfig[selectedGRNDetails.status].color
                          }
                          size="small"
                        />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        PO Number
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedGRNDetails.po_number}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Supplier
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {
                          selectedGRNDetails.po_reference.vendor_details
                            .supplier_name
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Warehouse
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedGRNDetails.warehouse_name}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Total Received Value
                      </Typography>
                      <Typography
                        variant="h6"
                        color="success.main"
                        fontWeight={600}
                      >
                        {formatCurrency(
                          selectedGRNDetails.total_received_value,
                          selectedGRNDetails.currency
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Received Date
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {dayjs(selectedGRNDetails.received_date).format(
                          "MMM DD, YYYY • HH:mm"
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Received By
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {selectedGRNDetails.received_by_name}
                      </Typography>
                    </Paper>
                  </Grid>
                  {selectedGRNDetails.delivery_receipt_number && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Delivery Receipt Number
                        </Typography>
                        <Typography variant="body2">
                          {selectedGRNDetails.delivery_receipt_number}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedGRNDetails.storage_location && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Storage Location
                        </Typography>
                        <Typography variant="body2">
                          {selectedGRNDetails.storage_location}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedGRNDetails.receiver_notes && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                        >
                          Receiver Notes
                        </Typography>
                        <Typography variant="body2">
                          {selectedGRNDetails.receiver_notes}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Purchase Order Reference
                      </Typography>
                      <Typography variant="body2">
                        <strong>PO Number:</strong>{" "}
                        {selectedGRNDetails.po_number}
                      </Typography>
                      <Typography variant="body2">
                        <strong>PO Total:</strong>{" "}
                        {formatCurrency(
                          selectedGRNDetails.po_reference.total_amount,
                          selectedGRNDetails.po_reference.currency
                        )}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Supplier:</strong>{" "}
                        {
                          selectedGRNDetails.po_reference.vendor_details
                            .supplier_name
                        }
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {drawerTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Received Items ({selectedGRNDetails.items.length})
                </Typography>
                {selectedGRNDetails.items &&
                selectedGRNDetails.items.length > 0 ? (
                  <Box>
                    {selectedGRNDetails.items.map((item, index) => (
                      <Paper
                        key={index}
                        sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Item Name
                            </Typography>
                            <Typography variant="body1" fontWeight={500}>
                              {item.item_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              SKU: {item.item_sku}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Ordered Qty
                            </Typography>
                            <Typography variant="body1">
                              {item.ordered_quantity} {item.unit}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Received Qty
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {item.received_quantity} {item.unit}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Unit Price
                            </Typography>
                            <Typography variant="body1">
                              {formatCurrency(
                                item.unit_price,
                                selectedGRNDetails.currency
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Condition
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={
                                  item.condition === "good"
                                    ? "Good"
                                    : item.condition === "damaged"
                                    ? "Damaged"
                                    : "Partially Damaged"
                                }
                                color={
                                  item.condition === "good"
                                    ? "success"
                                    : item.condition === "damaged"
                                    ? "error"
                                    : "warning"
                                }
                                size="small"
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              Total Cost
                            </Typography>
                            <Typography
                              variant="h6"
                              color="success.main"
                              fontWeight={600}
                            >
                              {formatCurrency(
                                item.total_cost,
                                selectedGRNDetails.currency
                              )}
                            </Typography>
                          </Grid>
                          {item.batch_number && (
                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Batch Number
                              </Typography>
                              <Typography variant="body1">
                                {item.batch_number}
                              </Typography>
                            </Grid>
                          )}
                          {item.expiry_date && (
                            <Grid item xs={6} sm={3}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                Expiry Date
                              </Typography>
                              <Typography variant="body1">
                                {dayjs(item.expiry_date).format("MMM DD, YYYY")}
                              </Typography>
                            </Grid>
                          )}
                          {item.remarks && (
                            <Grid item xs={12}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                                gutterBottom
                              >
                                Remarks
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  bgcolor: alpha("#FFC107", 0.1),
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: "1px solid #FFC107",
                                  fontStyle: "italic",
                                }}
                              >
                                {item.remarks}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info">
                    No items found in this goods receipt note.
                  </Alert>
                )}
              </Box>
            )}

            {drawerTab === 2 && (
              <Box sx={{ mt: 3 }}>
                <GRNActivityTimeline
                  activities={activityLogs}
                  loading={activityLoading}
                />
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* Dialogs */}
      <EnhancedApprovalDialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        grn={selectedGRN}
        onSuccess={(response: ApproveGRNResponse) => {
          loadGRNs();
          setApprovalDialogOpen(false);
          setSelectedGRN(null);

          // Show summary of ingredient operations
          if (response.ingredient_operations) {
            const {
              ingredients_created,
              ingredients_updated,
              batches_created,
            } = response.ingredient_operations;
            const summary = [];
            if (ingredients_created.length > 0)
              summary.push(
                `${ingredients_created.length} ingredient(s) created`
              );
            if (ingredients_updated.length > 0)
              summary.push(
                `${ingredients_updated.length} ingredient(s) updated`
              );
            if (batches_created.length > 0)
              summary.push(`${batches_created.length} batch(es) added`);

            if (summary.length > 0) {
              toast.success(
                `Ingredient operations completed: ${summary.join(", ")}`
              );
            }
          }
        }}
      />

      <RejectionDialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        grn={selectedGRN}
        onSuccess={() => {
          loadGRNs();
          setRejectionDialogOpen(false);
          setSelectedGRN(null);
        }}
      />

      <MarkInvoicedDialog
        open={markInvoicedDialogOpen}
        onClose={() => setMarkInvoicedDialogOpen(false)}
        grn={selectedGRN}
        onSuccess={() => {
          loadGRNs();
          setMarkInvoicedDialogOpen(false);
          setSelectedGRN(null);
        }}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDeleteGRN}
        title="GRN"
        itemName={selectedGRN?.grn_number || ""}
        itemDescription="This action cannot be undone."
        loading={saving}
      />
    </Fragment>
  );
};
